export type ErrorName =
  | "braze"
  | "login"
  | "restoreSession"
  | "linkWallet"
  | "decodePassportToken"
  | "passportRegistration"
  | "appError"
  | "checkoutWidgetError"
  | "createERC721Listing"
  | "createERC1155Listing"
  | "getTotalFee"
  | "signAndSubmitApproval"
  | "signAndSubmitOrder"
  | "signListing"
  | "createListing"
  | "prepareERC1155Listing"
  | "prepareERC721Listing"
  | "getListings"
  | "royaltyInfoError"
  | "fulfillERC721Bid"
  | "fulfillERC1155Bid"
  | "fetchCollectionBids"
  | "signEjectionTransaction"
  | "wishlist"
  | "getGame"
  | "getGames"
  | "getGameCollectionByTags"
  | "gamesPageStaticProps"
  | "gameDetailsPageStaticProps"
  | "useAccessToken"
  | "switchNetworkIfNeeded"
  | "connectionProviderConnect"
  | "connectWalletWidget"
  | "onRampWidget"
  | "getYourGem"
  | "fulfillERC1155CollectionBid"
  | "fulfillERC721CollectionBid"
  | "quests"
  | "fetchUser"
  | "logout"
  | "getImxProvider"
  | "authenticate"
  | "moonPaySignature"
  | "getTokens"
  | "getPreferences"
  | "updatePreferences"
  | "discordLogin"
  | "discordQuest"
  | "xLogin"
  | "abLandingStaticProps"
  | "calculatePointsRPCError"
  | "telegramQuest"
  | "twitchDropQuestClaimError"
  | "twitchLogin"
  | "riveFollowButton"
  | "gameTwitchDropsPageStaticProps"
  | "onboardingQuestsCompletionError"
  | "prismicQuestMappingError"
  | "getPrismicQuestsError"
  | "sessionActivity"
  | "discoverFeedError"
  | "onboardingFeedError"
  | "questCompletionError"
  | "paasCampaignStaticProps"
  | "getGamesDetailsPageError"
  | "xQuestData";

/**
 * https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/recordCustomEvent/#important-considerations-and-best-practices-include
 * Event types must encapsulate high-level categories and should be limited to 5 in total.
 * Use the attributes within the custom event to include event names for specificity.
 */
export type CustomEventType = "ViewAction";

export type CustomEventAttributes = {
  // Every event should have an eventName defined within the attributes.
  eventName: string;
  [key: string]: string | number | boolean | undefined;
};

export type MeasureAttributes = {
  start: number;
  end: number;
  customAttributes?: {
    [key: string]: string | number | boolean | undefined;
  };
};

/**
 * Ensures that a FRONTEND error is logged to NewRelic.
 *
 * @public
 * @remarks
 * This method should be used to log any frontend errors to NewRelic.
 * NOT to be used for logging backend errors.
 *
 * @param error - The first input number
 * @param name - The second input number
 * @returns void
 *
 */
export function notifyError(
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  error: any,
  name: ErrorName,
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  customAttributes: any | undefined = undefined,
) {
  const attributes = customAttributes
    ? { errorName: name, ...customAttributes }
    : { errorName: name };

  if (typeof window !== "undefined") {
    // @ts-ignore
    window.newrelic?.noticeError(error, attributes);
  }
}

/**
 * Records a custom event in New Relic.
 *
 * @param eventType - The type of the event.
 * @param attributes - The attributes of the event.
 * @returns void
 */
export function recordCustomEvent(
  eventType: CustomEventType,
  attributes: CustomEventAttributes,
) {
  if (typeof window !== "undefined") {
    // @ts-ignore
    window.newrelic?.recordCustomEvent(eventType, attributes);
  }
}

/**
 * https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/measure/
 * https://developer.mozilla.org/en-US/docs/Web/API/Performance/mark
 * Measure is used to track the duration of a specific event or flow.
 * For example, sign up + account initialisation, checkout, etc.
 * Example usage:
 *
 * const start = performance.mark("signupStart");
 * const end = performance.mark("signupEnd");
 * measure("signup", {
 *   start: start,
 *   end: end,
 *   customAttributes: {
 *     "walletAddress": "0x...",
 *   }
 * });
 */
export function measure(name: string, attributes: MeasureAttributes) {
  if (typeof window !== "undefined") {
    // @ts-ignore
    window.newrelic?.measure(name, attributes);
  }
}

export function setNewRelicUserId(userId: string | null) {
  if (typeof window !== "undefined") {
    // @ts-ignore
    window.newrelic?.setUserId(userId);
  }
}
